import {
  getCurrentShift as apiGetCurrentShift,
  openCashierShift as apiOpenCashierShift,
  closeCashierShift as apiCloseCashierShift,
  getShopCashRegisters as apiGetShopCashRegisters,
} from "@/lib/api";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";
import { indexedDBService } from "@/lib/db/indexed-db";

/**
 * Servicio frontend para comunicarse con la API de turnos
 * Solo maneja llamadas HTTP, no lógica de negocio
 */
export class ShiftApiService {
  /**
   * Obtener turno actual del usuario
   */
  async getCurrentShift(userId?: string) {
    try {
      const shop = getCurrentShop();

      // Usar userId proporcionado o obtener del contexto
      const user = userId ? { id: userId } : getCurrentUser();

      if (!shop || !user) {
        console.warn("⚠️ No hay tienda o usuario activo");
        return null;
      }

      const shift = await apiGetCurrentShift(shop.id, user.id);
      return shift;
    } catch (error) {
      console.error("❌ Error obteniendo turno:", error);
      return null;
    }
  }

  /**
   * Obtener cajas registradoras de la tienda
   */
  async getCashRegisters() {
    try {
      const shop = getCurrentShop();
      if (!shop) {
        console.warn("⚠️ No hay tienda activa");
        return [];
      }

      const registers = await apiGetShopCashRegisters(shop.id);
      // Cachear en IndexedDB para uso offline
      indexedDBService.saveCashRegisters(shop.id, registers).catch((err) =>
        console.warn("⚠️ No se pudo cachear cajas registradoras:", err),
      );
      return registers;
    } catch (error) {
      console.warn("⚠️ Error obteniendo cajas desde API, intentando caché local:", error);
      // Fallback offline: leer desde IndexedDB
      const shop = getCurrentShop();
      if (shop) {
        const cached = await indexedDBService.getCachedCashRegisters(shop.id);
        if (cached && cached.length > 0) {
          console.log("✅ Cajas registradoras cargadas desde caché offline:", cached.length);
          return cached;
        }
      }
      return [];
    }
  }

  /**
   * Abrir un nuevo turno
   */
  async openShift(
    cashRegisterId: string,
    initialCash: string,
    userId?: string,
  ) {
    const shop = getCurrentShop();

    // Usar userId proporcionado o obtener del contexto
    const user = userId
      ? { id: userId, fullName: "Usuario", email: "" }
      : getCurrentUser();

    if (!shop || !user) {
      throw new Error("No hay tienda o usuario activo");
    }

    if (!cashRegisterId || !initialCash) {
      throw new Error("Se requiere caja registradora y monto inicial");
    }

    try {
      const shift = await apiOpenCashierShift({
        shopId: shop.id,
        cashRegisterId,
        employeeId: user.id,
        initialCash,
        openedBy: user.fullName || user.email || "Usuario",
      });

      return shift;
    } catch (error: any) {
      throw new Error(error.message || "No se pudo abrir el turno");
    }
  }

  /**
   * Cerrar un turno con diferencia
   */
  async closeShift(
    shiftId: string,
    countedCash: string,
    notes: string = "",
    cashDifference?: string,
    discrepancyType?: string,
    denominationBreakdown?: Record<string, number>,
    authorizationCode?: string,
  ) {
    const user = getCurrentUser();

    if (!user) {
      throw new Error("No hay usuario activo");
    }

    if (!shiftId || !countedCash) {
      throw new Error("Se requiere ID de turno y efectivo contado");
    }

    try {
      const shift = await apiCloseCashierShift(shiftId, {
        countedCash,
        notes,
        closedBy: user.fullName || user.email || "Usuario",
        cashDifference, // NUEVO
        discrepancyType,
        denominationBreakdown,
        authorizationCode,
      });

      return shift;
    } catch (error: any) {
      console.error("❌ [shift-service] Error en closeShift:", error);
      // Propagar el error original sin modificar el mensaje
      // El ApiError ya viene con el mensaje correcto (incluye details del backend)
      throw error;
    }
  }
}

// Exportar instancia singleton
export const shiftApiService = new ShiftApiService();
